import type {
    FruitfulGoalType,
    MajorMoneyGoals,
} from '../../financial-goals/types/FinancialGoals';
import {
    type AreYouSavingJoint,
    type AreYouSavingSolo,
} from '../../financial-goals/types/Retirement';

export enum MembershipProfileCategory {
    PERSONAL_INFO = 'PERSONAL_INFO',
    FINANCIAL_GOALS = 'FINANCIAL_GOALS',
    RETIREMENT = 'RETIREMENT',
    CASH_FLOW = 'CASH_FLOW',
    NO_BOOKING = 'NO_BOOKING',
}

export enum QuestionKey {
    KIDS = 'KIDS',
    MONEY_GOALS = 'MONEY_GOALS',
    MONEY_GOALS_ONBOARDING_V2 = 'MONEY_GOALS_ONBOARDING_V2',
    FINANCIAL_FOCUS = 'FINANCIAL_FOCUS',
    ADDITIONAL_INFO = 'ADDITIONAL_INFO',
    NO_BOOKING_REASON = 'NO_BOOKING_REASON',
    SAVINGS = 'SAVINGS',
    DEBT = 'DEBT',
    RETIREMENT = 'RETIREMENT',
    SAVINGS_PRE_TAX = 'SAVINGS_PRE_TAX',
    SAVINGS_POST_TAX = 'SAVINGS_POST_TAX',
    INCOME = 'INCOME',
    SPENDING_FEELING = 'SPENDING_FEELING',
    BILLS = 'BILLS',
}

export enum HasKids {
    YES = 'Yes',
    NO = 'No',
    SOON = 'Soon',
    SOMEDAY = 'Someday',
}

export interface KidInfo {
    firstName?: string;
    birthYear: string;
}

export interface MembershipProfileKidAnswer {
    answer: HasKids;
    additionalInformation?: KidInfo[];
}

export interface MembershipProfileMoneyGoalAnswer {
    answer: MajorMoneyGoals;
}

export interface MembershipProfileRetirementAnswer {
    answer: AreYouSavingSolo | AreYouSavingJoint;
    additionalInformation?: string;
}

export interface MembershipProfileSavingsAnswer {
    answer: number;
}

export interface MembershipProfileIncomeAnswer {
    answer: number;
}

export interface MembershipProfileBillsAnswer {
    answer: number;
}

export interface MembershipProfileGoalsAnswer {
    answer: MajorMoneyGoals[];
}

export interface MembershipProfileFinancialFocusAnswer {
    answer: FruitfulGoalType;
}

export interface MembershipProfileAdditionalInfoAnswer {
    answer: string;
}

export interface MembershipProfileNoBookingAnswer {
    answer: string;
}

export interface MembershipProfileCashFlowAnswer {
    answer: string;
}

export enum SpendingFeeling {
    SHORT = 'SHORT',
    HAVE_MONEY_LEFT_OVER = 'HAVE_MONEY_LEFT_OVER',
    BREAK_EVEN = 'BREAK_EVEN',
}

export interface MembershipProfileSpendingFeelingAnswer {
    answer: SpendingFeeling;
}

export type ProfileAnswer =
    | MembershipProfileKidAnswer
    | MembershipProfileMoneyGoalAnswer[]
    | MembershipProfileFinancialFocusAnswer[]
    | MembershipProfileAdditionalInfoAnswer
    | MembershipProfileNoBookingAnswer
    | MembershipProfileSavingsAnswer
    | MembershipProfileRetirementAnswer
    | MembershipProfileCashFlowAnswer
    | MembershipProfileIncomeAnswer
    | MembershipProfileSpendingFeelingAnswer
    | MembershipProfileBillsAnswer
    | MembershipProfileGoalsAnswer;

export interface MembershipProfileAnswerUpsertDto<T extends ProfileAnswer> {
    membershipId: string;
    answeredByUserId: string;
    questionKey: QuestionKey;
    answer: T;
}

export interface MembershipProfileQuestion {
    id: string;
    category: MembershipProfileCategory;
    questionKey: QuestionKey;
    createdAt: Date;
    updatedAt: Date | null;
}

export interface MembershipProfileAnswer<T extends ProfileAnswer> {
    id: string;
    membershipId: string;
    answeredByUserId: string;
    membershipProfileQuestionId: string;
    answer: T;
    createdAt: Date;
    updatedAt: Date | null;
}

export interface MembershipProfileAnswerWithQuestion<
    T extends ProfileAnswer,
> extends MembershipProfileAnswer<T> {
    membershipProfileQuestion: MembershipProfileQuestion;
}
